import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const PartnersEditor = () => {
  const [partners, setPartners] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    order: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/partners`);
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingPartner) {
        await axios.put(`${BACKEND_URL}/api/admin/partners/${editingPartner.id}`, formData);
        toast({ title: 'Success', description: 'Partner updated successfully' });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/partners`, formData);
        toast({ title: 'Success', description: 'Partner created successfully' });
      }
      setIsOpen(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save partner', variant: 'destructive' });
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      logo_url: partner.logo_url || '',
      order: partner.order
    });
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/partners/${id}`);
      toast({ title: 'Success', description: 'Partner deleted successfully' });
      fetchPartners();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete partner', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ name: '', logo_url: '', order: 0 });
    setEditingPartner(null);
  };

  return (
    <Card className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Partners</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-pink-500 hover:bg-pink-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Partner
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingPartner ? 'Edit Partner' : 'Add Partner'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Partner Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Hues"
                />
              </div>
              <div>
                <Label htmlFor="logo_url">Logo URL (Optional)</Label>
                <Input
                  id="logo_url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="Paste from Media Library"
                />
              </div>
              <div>
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                />
              </div>
              <Button onClick={handleSubmit} className="w-full">
                {editingPartner ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {partners.map((partner) => (
          <Card key={partner.id} className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg text-pink-500">{partner.name}</h3>
              <div className="flex space-x-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(partner)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(partner.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {partner.logo_url && (
              <img src={partner.logo_url} alt={partner.name} className="w-full h-20 object-contain" />
            )}
            <p className="text-sm text-gray-500 mt-2">Order: {partner.order}</p>
          </Card>
        ))}
      </div>

      {partners.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No partners added yet
        </div>
      )}
    </Card>
  );
};

export default PartnersEditor;
